import { Grade, type Item, type Receipt, ReceiptCreate, type ReceiptLineCreate } from '@morales/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { api, ApiError, newIdempotencyKey } from '../api';
import { useAuth } from '../auth';
import { StockNav } from '../components/StockNav';
import { Empty, ErrorBox, QueryView, SearchBox } from '../components/ui';
import { formatMoney, formatStamp, packLabel } from '../format';
import { useDebounced } from '../hooks';
import { useLocale } from '../i18n';
import { useSite } from '../sites';
import type { AppMessageKey } from '../messages';
import { qk } from '../queryClient';
import { parseNumber } from './StockPage';

interface Line {
  itemId: string;
  itemName: string;
  pack: string;
  qty: string;
  unitCost: string;
  grade: Grade;
  origin: string;
}

/** The lines the API wants, or a message key when something typed is not usable. */
export function toReceiptLines(lines: Line[], withCost: boolean): { lines: ReceiptLineCreate[] } | { problem: AppMessageKey } {
  if (lines.length === 0) return { problem: 'nothingToSave' };
  const out: ReceiptLineCreate[] = [];
  for (const l of lines) {
    const qty = parseNumber(l.qty);
    if (qty === null || qty <= 0) return { problem: 'receiveQtyBad' };
    const cost = withCost ? parseNumber(l.unitCost) : null;
    if (withCost && l.unitCost.trim() !== '' && cost === null) return { problem: 'receiveCostBad' };
    out.push({
      itemId: l.itemId,
      qty,
      grade: l.grade,
      ...(cost !== null && l.unitCost.trim() !== '' ? { unitCost: cost } : {}),
      ...(l.origin.trim() ? { origin: l.origin.trim() } : {}),
    });
  }
  return { lines: out };
}

export function ReceivePage() {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const qc = useQueryClient();
  const { siteId } = useSite();
  const withCost = me?.role === 'owner' || me?.role === 'office';
  const [vendorId, setVendorId] = useState('');
  const [ticket, setTicket] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [picking, setPicking] = useState(false);
  const [problem, setProblem] = useState<AppMessageKey | null>(null);
  const [saved, setSaved] = useState<Receipt | null>(null);
  const attempt = useRef<{ body: string; key: string } | null>(null);

  const vendors = useQuery({ queryKey: qk.vendors, queryFn: ({ signal }) => api.vendors(signal) });
  const recent = useQuery({ queryKey: qk.receipts('recent'), queryFn: ({ signal }) => api.receipts({}, signal) });
  const pos = useQuery({ queryKey: qk.purchaseOrders, queryFn: ({ signal }) => api.purchaseOrders(signal) });
  const [poId, setPoId] = useState('');
  const [freight, setFreight] = useState('');
  const [fees, setFees] = useState('');
  const [allocate, setAllocate] = useState<'packs' | 'weight'>('packs');
  const openPos = (pos.data ?? []).filter((po) => po.vendorId === vendorId);
  const save = useMutation({
    mutationFn: (v: { body: ReceiptCreate; key: string }) => api.receive(v.body, v.key),
    retry: (count, error) => error instanceof ApiError && error.status === 0 && count < 3,
    onSuccess: (receipt) => {
      attempt.current = null;
      setLines([]);
      setTicket('');
      setPoId('');
      setFreight('');
      setFees('');
      setSaved(receipt);
      void qc.invalidateQueries({ queryKey: qk.purchaseOrders });
      void qc.invalidateQueries({ queryKey: ['receipts'] });
      void qc.invalidateQueries({ queryKey: ['stock'] });
      void qc.invalidateQueries({ queryKey: ['items'] });
    },
    onError: (error) => {
      if (!(error instanceof ApiError && error.status === 0)) attempt.current = null;
    },
  });

  const edit = (i: number, change: Partial<Line>) => {
    setProblem(null);
    setLines((cur) => cur.map((l, j) => (j === i ? { ...l, ...change } : l)));
  };
  function submit() {
    if (!vendorId) return setProblem('vendor_required');
    const r = toReceiptLines(lines, withCost);
    if ('problem' in r) return setProblem(r.problem);
    const parsed = ReceiptCreate.safeParse({ vendorId, ...(siteId ? { siteId } : {}), ...(poId ? { purchaseOrderId: poId } : {}), ...(ticket.trim() ? { documentRef: ticket.trim() } : {}), ...(withCost ? { freight: parseNumber(freight) ?? 0, fees: parseNumber(fees) ?? 0, allocate } : {}), lines: r.lines });
    if (!parsed.success) return setProblem('error');
    setProblem(null);
    setSaved(null);
    const serialized = JSON.stringify(parsed.data);
    if (attempt.current?.body !== serialized) attempt.current = { body: serialized, key: newIdempotencyKey() };
    save.mutate({ body: parsed.data, key: attempt.current.key });
  }
  const totalPacks = lines.reduce((s, l) => s + (parseNumber(l.qty) ?? 0), 0);

  return (
    <div className="stack">
      <section className="stack-sm">
        <h1>{t('receiveTitle')}</h1>
        <StockNav />
        <p className="muted">{t('receiveHint')}</p>
      </section>
      {saved ? (
        <div className="flash" role="status">
          ✓ {t('received')}: {saved.vendorName} · {saved.totalPacks} {t('receivedPacks')}
          {saved.totalCost !== null ? ` · ${formatMoney(saved.totalCost, intlLocale)}` : ''}
          {saved.totalLanded !== null && saved.totalLanded !== saved.totalCost ? ` · ${formatMoney(saved.totalLanded, intlLocale)} ${t('landed')}` : ''}
        </div>
      ) : null}
      <div className="picker-head">
        <label className="field" style={{ flex: 1 }}>
          <span>{t('vendor')}</span>
          <select value={vendorId} onChange={(e) => setVendorId(e.target.value)} aria-label={t('vendor')}>
            <option value="">—</option>
            {(vendors.data ?? []).map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field" style={{ flex: 1 }}>
          <span>{t('vendorTicket')}</span>
          <input type="text" autoComplete="off" value={ticket} onChange={(e) => setTicket(e.target.value)} />
        </label>
      </div>
      {withCost ? (
        <div className="picker-head" style={{ flexWrap: 'wrap' }}>
          <label className="field" style={{ flex: '1 1 110px' }}>
            <span>{t('freight')}</span>
            <input type="text" inputMode="decimal" autoComplete="off" placeholder="$" value={freight} onChange={(e) => setFreight(e.target.value)} />
          </label>
          <label className="field" style={{ flex: '1 1 110px' }}>
            <span>{t('fees')}</span>
            <input type="text" inputMode="decimal" autoComplete="off" placeholder="$" value={fees} onChange={(e) => setFees(e.target.value)} />
          </label>
          <label className="field" style={{ flex: '1 1 110px' }}>
            <span>{t('allocate')}</span>
            <select value={allocate} aria-label={t('allocate')} onChange={(e) => setAllocate(e.target.value === 'weight' ? 'weight' : 'packs')}>
              <option value="packs">{t('allocate_packs')}</option>
              <option value="weight">{t('allocate_weight')}</option>
            </select>
          </label>
        </div>
      ) : null}
      {vendorId && openPos.length > 0 ? (
        <label className="field">
          <span>{t('fromPo')}</span>
          <select
            value={poId}
            aria-label={t('fromPo')}
            onChange={(e) => {
              const id = e.target.value;
              setPoId(id);
              const po = openPos.find((x) => x.id === id);
              if (po) {
                setLines(
                  po.lines
                    // a second drop: lines already received in full (or ordered as 0) have nothing left to receive
                    .filter((l) => l.itemId && l.qtyOrdered - (l.qtyReceived ?? 0) > 0)
                    .map((l) => ({ itemId: l.itemId!, itemName: l.itemName ?? '', pack: l.unit ?? '', qty: String(Math.max(0, l.qtyOrdered - (l.qtyReceived ?? 0))), unitCost: l.unitCost !== null ? String(l.unitCost) : '', grade: 'good' as const, origin: '' })),
                );
              }
            }}
          >
            <option value="">—</option>
            {openPos.map((po) => (
              <option key={po.id} value={po.id}>
                {po.poNo ?? po.id.slice(0, 8)} · {po.totalPacks} {t('packs')}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {lines.length > 0 ? (
        <ul className="list">
          {lines.map((l, i) => (
            <li key={`${l.itemId}:${i}`} className="count-row stack-sm">
              <div className="row count-head">
                <span className="row-main">
                  <span className="row-title">{l.itemName}</span>
                  <span className="row-sub">{l.pack}</span>
                </span>
                <label className="count-field">
                  <span className="label">{t('qty')}</span>
                  <input
                    className={parseNumber(l.qty) ? 'count-input filled' : 'count-input'}
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    placeholder="—"
                    aria-label={`${t('qty')}: ${l.itemName}`}
                    value={l.qty}
                    onChange={(e) => edit(i, { qty: e.target.value })}
                  />
                </label>
              </div>
              <div className="count-open">
                <div className="chips" role="group" aria-label={`${t('grade')}: ${l.itemName}`}>
                  {Grade.options.map((g) => (
                    <button key={g} type="button" className="chip" aria-pressed={l.grade === g} onClick={() => edit(i, { grade: g })}>
                      {t(`grade_${g}`)}
                    </button>
                  ))}
                </div>
                {withCost ? (
                  <input className="count-input" type="text" inputMode="decimal" autoComplete="off" placeholder={t('unitCost')} aria-label={`${t('unitCost')}: ${l.itemName}`} value={l.unitCost} onChange={(e) => edit(i, { unitCost: e.target.value })} />
                ) : null}
                <input type="text" autoComplete="off" placeholder={t('origin')} aria-label={`${t('origin')}: ${l.itemName}`} value={l.origin} onChange={(e) => edit(i, { origin: e.target.value })} style={{ flex: 1, minWidth: 120 }} />
                <button type="button" className="btn btn-ghost" aria-label={`${t('remove')}: ${l.itemName}`} onClick={() => setLines((cur) => cur.filter((_, j) => j !== i))}>
                  {t('remove')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {picking ? (
        <ItemPicker
          onPick={(item) => {
            setLines((cur) => [...cur, { itemId: item.id, itemName: item.name, pack: packLabel(item), qty: '', unitCost: '', grade: 'good', origin: '' }]);
            setPicking(false);
          }}
          onCancel={() => setPicking(false)}
        />
      ) : (
        <button type="button" className="btn btn-secondary btn-lg btn-block" onClick={() => setPicking(true)}>
          + {t('addItem')}
        </button>
      )}
      {problem ? (
        <p className="field-error" role="alert">
          {t(problem)}
        </p>
      ) : null}
      {save.error ? <ErrorBox error={save.error} /> : null}

      <section className="card stack-sm">
        <h2>{t('deliveries')}</h2>
        <QueryView query={recent}>
          {(rows) =>
            rows.length === 0 ? (
              <Empty>{t('noResults')}</Empty>
            ) : (
              <ul className="list">
                {rows.slice(0, 10).map((r) => (
                  <li key={r.id} className="row">
                    <span className="row-main">
                      <span className="row-title">{r.vendorName}</span>
                      <span className="row-sub">
                        {formatStamp(r.receivedAt, intlLocale, 'date')}
                        {r.documentRef ? ` · ${r.documentRef}` : ''}
                        {r.receivedBy ? ` · ${r.receivedBy}` : ''}
                      </span>
                    </span>
                    <span className="row-end">
                      <span className="row-end-value">{r.totalPacks}</span>
                      <span className="row-end-label">{r.lineCount} {t('lines')}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )
          }
        </QueryView>
      </section>

      {lines.length > 0 ? (
        <div className="actionbar">
          <div className="total">
            <span>{t('received')}</span>
            <strong>
              {totalPacks} {t('receivedPacks')}
            </strong>
          </div>
          <button type="button" className="btn btn-primary btn-lg" onClick={submit} disabled={save.isPending}>
            {save.isPending ? t('sending') : t('save')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function ItemPicker({ onPick, onCancel }: { onPick: (item: Item) => void; onCancel: () => void }) {
  const { t } = useLocale();
  const [q, setQ] = useState('');
  const term = useDebounced(q.trim());
  const list = useQuery({ queryKey: qk.items(term), queryFn: ({ signal }) => api.items(term, signal), placeholderData: keepPreviousData });
  return (
    <div className="picker">
      <div className="picker-head">
        <SearchBox value={q} onChange={setQ} autoFocus />
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          {t('cancel')}
        </button>
      </div>
      <QueryView query={list}>
        {(rows) => {
          const shown = rows.filter((i) => i.active).slice(0, 30);
          return shown.length === 0 ? (
            <Empty>{t('noResults')}</Empty>
          ) : (
            <ul className="list">
              {shown.map((item) => (
                <li key={item.id}>
                  <button type="button" className="row" onClick={() => onPick(item)}>
                    <span className="row-main">
                      <span className="row-title">{item.name}</span>
                      <span className="row-sub">
                        {item.sku ? <span className="label">{item.sku}</span> : null}
                        {packLabel(item)}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          );
        }}
      </QueryView>
    </div>
  );
}

